import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClosingOutcome } from "@shared/closingMath";

// Client bindings for the closed-deal outcome API (server/closing-routes.ts).
// Rows come back snake_case, matching that endpoint's convention. Note the
// clients LIST endpoint returns the same data camelCase; that difference is
// intentional per-endpoint consistency, not a bug.

export interface Closing {
  company_id: string;
  outcome: ClosingOutcome;
  closed_on: string; // plain YYYY-MM-DD, never a timestamp
  acquirer_company_id: string | null;
  acquirer_name: string | null;
  acquirer_company_name: string | null;
  final_ev: string | null; // decimal arrives as string from pg
  ebitda_at_close: string | null;
  fee_collected: string | null; // gated: absent for users without the financials grant
  notes: string | null;
  recorded_at: string;
}

export function useClosing(companyId: string | undefined) {
  return useQuery<Closing | null>({
    queryKey: ["closing", companyId],
    queryFn: async () => {
      const r = await fetch(`/api/companies/${companyId}/closing`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load closing");
      return r.json();
    },
    enabled: !!companyId,
  });
}

export function useClosingMutations(companyId: string | undefined) {
  const qc = useQueryClient();
  // A closing changes the client's status, so it invalidates the company and
  // clients list too, plus the dashboard (which now excludes closed clients
  // from pipeline counts).
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["closing", companyId] });
    qc.invalidateQueries({ queryKey: ["company", companyId] });
    qc.invalidateQueries({ queryKey: ["financials-fee-detail", companyId] });
    qc.invalidateQueries({ queryKey: ["clients"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  return {
    save: useMutation({
      mutationFn: async (body: Record<string, unknown>) => {
        const r = await fetch(`/api/companies/${companyId}/closing`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Failed to save closing");
        return j as Closing;
      },
      onSuccess: invalidate,
    }),
    reopen: useMutation({
      mutationFn: async () => {
        const r = await fetch(`/api/companies/${companyId}/closing`, {
          method: "DELETE",
          credentials: "include",
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Failed to reopen deal");
        return j as { deleted: number };
      },
      onSuccess: invalidate,
    }),
  };
}
